// File: src/pages/registration/Register.js

import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import axios from 'axios';

import {
  track,
  identifyUser,
} from '../../utils/analytics';

import { IoArrowBackSharp } from 'react-icons/io5';
import { ReactComponent as TribeLogo } from '../../assets/TribeLogo.svg';
import MinimalFooter from '../../components/MinimalFooter';

import {
  FaRegImage,
  FaFolderOpen,
} from 'react-icons/fa';

import { useToaster } from '../../context/ToasterContext';
import UnsavedChangesBottomSheet from '../../components/UnsavedChangesBottomSheet';

import {
  renderStepContent,
  ProgressPill,
} from './RegisterSteps';

import {
  pageStyle,
  blackBackButtonStyle,
  topBarStyleStep1,
  toggleContainerStyle,
  toggleItemStyle,
  containerStyle,
  headerContainerStyle,
  headerLogoRowStyle,
  headerNavRowStyle,
  previousBackButtonStyle,
  pageTitlePlainStyle,
  progressContainerStyle,
  actionSheetStyles,
  logoSvgStyle,
} from './RegisterStyles';

function birthDateISO(value) {
  const raw = String(value || '').trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  const us = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  const [year, month, day] = iso ? iso.slice(1).map(Number) : us ? [Number(us[3]), Number(us[1]), Number(us[2])] : [];
  const date = new Date(Date.UTC(year, month - 1, day));
  if (!year || year < 1900 || date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return '';
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function adultBirthDateLimit(now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now).map(part => [part.type, Number(part.value)]));
  const year = parts.year - 18;
  const day = Math.min(parts.day, new Date(Date.UTC(year, parts.month, 0)).getUTCDate());
  return `${year}-${String(parts.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function isAdultBirthDate(value, now = new Date()) {
  const iso = birthDateISO(value);
  return Boolean(iso && iso <= adultBirthDateLimit(now));
}

function birthDateDisplay(value) {
  const iso = birthDateISO(value);
  return iso ? `${iso.slice(5, 7)}/${iso.slice(8, 10)}/${iso.slice(0, 4)}` : '';
}

function getRegistrationStepName(step) {
  switch (step) {
    case 1:
      return 'invite_code';
    case 2:
      return 'basic_info';
    case 3:
      return 'verification_code';
    case 4:
      return 'password';
    case 5:
      return 'phone';
    case 5.5:
      return 'phone_verification';
    case 6:
      return 'sms_consent';
    case 7:
      return 'gender_dob';
    case 8:
      return 'profile_picture';
    case 9:
      return 'interests';
    case 10:
      return 'terms';
    default:
      return 'unknown';
  }
}
export default function Register() {
  const navigate = useNavigate();

  const {
    handleLoginSuccess,
  } = useContext(AuthContext);

  const {
    addToast,
  } = useToaster();

  const [
    showUnsavedSheet,
    setShowUnsavedSheet,
  ] = useState(false);

  const [
    step,
    setStep,
  ] = useState(1);

  const [
    firstName,
    setFirstName,
  ] = useState('');

  const [
    lastName,
    setLastName,
  ] = useState('');

  const [
    email,
    setEmail,
  ] = useState('');

  const [
    inviteCode,
    setInviteCode,
  ] = useState(() => {
    const code = new URLSearchParams(window.location.hash.slice(1)).get('inviteCode') || new URLSearchParams(window.location.search).get('inviteCode') || '';
    return /^[a-z0-9-]{1,64}$/i.test(code.trim()) ? code.trim().toUpperCase() : '';
  });

  const [
    verificationCode,
    setVerificationCode,
  ] = useState('');

  const [
    isCodeValid,
    setIsCodeValid,
  ] = useState(false);

  const [
    shakeInputs,
    setShakeInputs,
  ] = useState(false);

  const [
    shakeCode,
    setShakeCode,
  ] = useState(false);

  const [
    password,
    setPassword,
  ] = useState('');

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState('');

  const [
    phoneNumber,
    setPhoneNumber,
  ] = useState('');

  const [
    smsOptIn,
    setSmsOptIn,
  ] = useState(null);

  const [
    showSmsTermsReminder,
    setShowSmsTermsReminder,
  ] = useState(false);

  const [
    segFocus,
    setSegFocus,
  ] = useState({
    seg2: false,
    seg3: false,
    seg4: false,
  });

  const [
    gender,
    setGender,
  ] = useState('');

  const [
    showGenderMenu,
    setShowGenderMenu,
  ] = useState(false);

  const [
    dateOfBirth,
    setDateOfBirth,
  ] = useState('');

  const [
    typedBirthday,
    setTypedBirthday,
  ] = useState('');

  const fileInputRef = useRef(null);
  const registrationContentRef = useRef(null);

  const [
    profilePicFile,
    setProfilePicFile,
  ] = useState(null);

  const [
    profilePicPreview,
    setProfilePicPreview,
  ] = useState(null);

  const [
    showProfileActionSheet,
    setShowProfileActionSheet,
  ] = useState(false);

  const [
    interests,
    setInterests,
  ] = useState([]);

  const [
    customInterest,
    setCustomInterest,
  ] = useState('');

  const [
    checkedTerms,
    setCheckedTerms,
  ] = useState(false);

  const [
    isSubmitting,
    setIsSubmitting,
  ] = useState(false);

  const [
    isSendingCode,
    setIsSendingCode,
  ] = useState(false);

  // Refs lock immediately, before React has time to re-render.
  // This prevents rapid double-clicks from creating duplicate requests.
  const sendCodeLockRef = useRef(false);
  const submitLockRef = useRef(false);

  const [phoneChallenge, setPhoneChallenge] = useState(null);
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneProof, setPhoneProof] = useState(null);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneResendAt, setPhoneResendAt] = useState(0);
  const [phoneClock, setPhoneClock] = useState(Date.now());
  const phoneLock = useRef(false);
  const phoneIdentity = useRef('');
  phoneIdentity.current = `${email.trim().toLowerCase()}|${phoneNumber}|${inviteCode}`;
  const [applicationPrefill, setApplicationPrefill] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(() => /^MEM-/i.test(inviteCode));
  const [prefillError, setPrefillError] = useState('');
  const initialInviteCode = useRef(inviteCode);
  const inviteValidationLock = useRef(false);
  const useApplicationPhoto = Boolean(applicationPrefill?.profilePicUrl && !profilePicFile);
  const applicationEmailUnchanged = Boolean(applicationPrefill && applicationPrefill.inviteCode === inviteCode.trim().toUpperCase() && applicationPrefill.email === email.trim().toLowerCase());
  const applicationPhoneUnchanged = Boolean(applicationPrefill?.phoneVerifiedAt && applicationPrefill.inviteCode === inviteCode.trim().toUpperCase() && applicationPrefill.phoneNumber === phoneNumber);
  const phoneIsVerified = applicationPhoneUnchanged || Boolean(phoneProof && phoneProof.email === email.trim().toLowerCase() && phoneProof.phoneNumber === phoneNumber && phoneProof.expiresAt > Date.now());
  const phoneWaitSeconds = Math.max(0, Math.ceil((phoneResendAt - phoneClock) / 1000));
  const registrationSteps = [1, 2, ...(!applicationEmailUnchanged ? [3] : []), 4, 5, ...(!applicationPhoneUnchanged ? [5.5] : []), 6, 7, 8, 9, 10];
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const visibleHeight = () => viewport.height * (viewport.scale || 1);
    let restingHeight = visibleHeight(), layoutWidth = window.innerWidth;
    let keyboardOpen = false, keyboardHeight = restingHeight, scrollingBack = false, scrollFrame;
    const editable = element => Boolean(element && registrationContentRef.current?.contains(element)
      && !element.readOnly && !element.disabled
      && (element.tagName === 'TEXTAREA' || (element.tagName === 'INPUT'
        && ['text', 'email', 'tel', 'number', 'password', 'search', 'url'].includes(element.type))));
    const covered = () => restingHeight - visibleHeight() > Math.max(120, restingHeight * 0.18);
    const cancelReset = () => {
      window.cancelAnimationFrame(scrollFrame); scrollFrame = undefined;
      if (scrollingBack) window.scrollTo({ top: window.scrollY, left: window.scrollX, behavior: 'instant' });
      scrollingBack = false;
    };
    const returnToTop = () => {
      keyboardOpen = false;
      if (scrollFrame !== undefined) return;
      scrollFrame = window.requestAnimationFrame(() => {
        scrollFrame = undefined;
        if (document.body.style.overflow === 'hidden') return;
        if (editable(document.activeElement)) document.activeElement.blur();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        scrollingBack = !reduceMotion;
        window.scrollTo({ top: 0, left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    };
    const focusField = event => {
      if (!editable(event.target)) return;
      // A focus handoff between fields cancels the return before the next paint.
      cancelReset(); restingHeight = Math.max(restingHeight, visibleHeight());
      keyboardOpen = covered(); keyboardHeight = visibleHeight();
    };
    const blurField = event => {
      // Start with dismissal, without waiting for the keyboard's viewport animation.
      if (editable(event.target) && keyboardOpen) returnToTop();
    };
    const resize = () => {
      const height = visibleHeight();
      if (window.innerWidth !== layoutWidth) {
        layoutWidth = window.innerWidth; restingHeight = height; keyboardHeight = height;
        keyboardOpen = false; cancelReset(); return;
      }
      if (scrollFrame !== undefined) return;
      if (keyboardOpen) {
        keyboardHeight = Math.min(keyboardHeight, height);
        // Some keyboards hide without blurring. Start during recovery, not at full height.
        // Ignore small changes such as the keyboard's suggestion row.
        const closingGrowth = Math.max(96, (restingHeight - keyboardHeight) * 0.35);
        if (height - keyboardHeight >= closingGrowth || restingHeight - height <= 80) returnToTop();
      } else if (editable(document.activeElement) && covered()) {
        keyboardOpen = true; keyboardHeight = height; cancelReset();
      } else if (!editable(document.activeElement)) {
        restingHeight = Math.max(restingHeight, height);
      }
    };
    document.addEventListener('focusin', focusField);
    document.addEventListener('focusout', blurField);
    viewport.addEventListener('resize', resize);
    return () => {
      cancelReset(); document.removeEventListener('focusin', focusField);
      document.removeEventListener('focusout', blurField); viewport.removeEventListener('resize', resize);
    };
  }, []);
  useEffect(() => { setPhoneChallenge(null); setPhoneCode(''); setPhoneProof(null); }, [email, phoneNumber, inviteCode]);
  useEffect(() => {
    if (step !== 5.5 || phoneResendAt <= Date.now()) return undefined;
    const timer = window.setInterval(() => setPhoneClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [step, phoneResendAt]);
  useEffect(() => {
    if (!phoneProof || applicationPhoneUnchanged) return undefined;
    const timer = window.setTimeout(() => {
      setPhoneProof(null); setPhoneChallenge(null); setPhoneCode('');
      setStep(current => current > 5 ? 5 : current);
      addToast('Your phone verification expired. Verify your number again.', 'error');
    }, Math.min(2147483647, Math.max(0, phoneProof.expiresAt - Date.now())));
    return () => window.clearTimeout(timer);
  }, [phoneProof, applicationPhoneUnchanged, addToast]);
  const applyRegistrationPrefill = useCallback((data, code) => {
    const saved = data?.registrationPrefill;
    if (!saved) { setApplicationPrefill(null); return; }
    if (!code.startsWith('MEM-') || !saved.email || !saved.firstName || !saved.lastName || !saved.phoneNumber) throw new Error('Could not load your application details.');
    setApplicationPrefill({ ...saved, inviteCode: code });
    setFirstName(saved.firstName); setLastName(saved.lastName); setEmail(saved.email);
    setPhoneNumber(saved.phoneNumber);
    setGender(['male', 'female', 'non-binary', 'trans-man', 'trans-woman'].includes(saved.gender) ? saved.gender : '');
    setDateOfBirth(birthDateDisplay(saved.dateOfBirth));
    setTypedBirthday(birthDateDisplay(saved.dateOfBirth));
    setProfilePicFile(null); setProfilePicPreview(saved.profilePicUrl || null);
  }, []);

  useEffect(() => {
    const code = initialInviteCode.current;
    if (!code.startsWith('MEM-')) return undefined;
    const controller = new AbortController(); let cancelled = false;
    // Keep the bearer invite out of copied URLs and subsequent navigation.
    const clean = new URL(window.location.href);
    clean.searchParams.delete('inviteCode');
    const fragment = new URLSearchParams(clean.hash.slice(1)); fragment.delete('inviteCode'); clean.hash = fragment.toString();
    window.history.replaceState(window.history.state, '', clean.pathname + clean.search + clean.hash);
    axios.post(`${process.env.REACT_APP_API_URL}/users/validateInviteCode`, { inviteCode: code }, { signal: controller.signal }).then(response => {
      if (cancelled) return;
      if (!response.data?.valid || !response.data.registrationPrefill) throw new Error('This invitation is no longer available.');
      applyRegistrationPrefill(response.data, code); setStep(2);
    }).catch(error => {
      if (!cancelled) setPrefillError(error.response?.data?.message || error.message || 'Could not load your invitation. Try your code again.');
    }).finally(() => { if (!cancelled) setPrefillLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [applyRegistrationPrefill]);

  function changeRegistrationEmail(value) {
    if (value.trim().toLowerCase() !== email.trim().toLowerCase()) {
      setVerificationCode(''); setIsCodeValid(false);
    }
    setEmail(value);
  }

  function changeInviteCode(value) {
    if (applicationPrefill && value.trim().toUpperCase() !== applicationPrefill.inviteCode) {
      setApplicationPrefill(null); setFirstName(''); setLastName(''); setEmail('');
      setPhoneNumber(''); setGender(''); setDateOfBirth(''); setTypedBirthday('');
      setProfilePicFile(null); setProfilePicPreview(null); setVerificationCode(''); setIsCodeValid(false);
    }
    setPrefillError(''); setInviteCode(value);
  }

  const MAX_SIZE_BYTES =
    15 * 1024 * 1024;

  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
  ];

  useEffect(() => {
    if (showProfileActionSheet) {
      document.documentElement.style.overflow =
        'hidden';

      document.body.style.overflow =
        'hidden';

      document.documentElement.style.position =
        'fixed';

      document.body.style.position =
        'fixed';
    } else {
      document.documentElement.style.overflow =
        '';

      document.body.style.overflow =
        '';

      document.documentElement.style.position =
        '';

      document.body.style.position =
        '';
    }

    return () => {
      document.documentElement.style.overflow =
        '';

      document.body.style.overflow =
        '';

      document.documentElement.style.position =
        '';

      document.body.style.position =
        '';
    };
  }, [showProfileActionSheet]);


  useEffect(() => {
    track(
      'registration_step_viewed',
      {
        step,
        step_name:
          getRegistrationStepName(
            step
          ),
      }
    );
  }, [step]);

  useEffect(() => {
    let timeout;

    const resetTimer = () => {
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        track(
          'registration_inactive',
          {
            step,

            step_name:
              getRegistrationStepName(
                step
              ),
          }
        );
      }, 45000);
    };

    window.addEventListener(
      'mousemove',
      resetTimer
    );

    window.addEventListener(
      'keydown',
      resetTimer
    );

    window.addEventListener(
      'touchstart',
      resetTimer
    );

    window.addEventListener(
      'scroll',
      resetTimer
    );

    resetTimer();

    return () => {
      clearTimeout(timeout);

      window.removeEventListener(
        'mousemove',
        resetTimer
      );

      window.removeEventListener(
        'keydown',
        resetTimer
      );

      window.removeEventListener(
        'touchstart',
        resetTimer
      );

      window.removeEventListener(
        'scroll',
        resetTimer
      );
    };
  }, [step]);

  async function requestPhoneVerification(advance = false) {
    if (phoneLock.current || (!advance && phoneResendAt > Date.now())) return;
    phoneLock.current = true; setPhoneBusy(true);
    const identity = phoneIdentity.current;
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/membership-applications/phone/send-code`, {
        purpose: 'registration', email: email.trim().toLowerCase(), phoneNumber, inviteCode,
      });
      if (identity !== phoneIdentity.current) return;
      if (!response.data?.challengeId) throw new Error('Verification text not confirmed.');
      setPhoneChallenge(response.data.challengeId); setPhoneCode(''); setPhoneProof(null);
      setPhoneResendAt(Date.now() + 60000); setPhoneClock(Date.now());
      if (advance) setStep(5.5);
    } catch (failure) { addToast(failure.response?.data?.message || 'We couldn’t send your verification text. Please try again.', 'error'); }
    finally { phoneLock.current = false; setPhoneBusy(false); }
  }
  async function confirmPhoneVerification() {
    if (phoneLock.current || !phoneChallenge || !/^\d{3}$/.test(phoneCode)) return;
    phoneLock.current = true; setPhoneBusy(true);
    const identity = phoneIdentity.current;
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/membership-applications/phone/verify`, {
        purpose: 'registration', email: email.trim().toLowerCase(), phoneNumber, challengeId: phoneChallenge, code: phoneCode,
      });
      if (identity !== phoneIdentity.current) return;
      const result = response.data, expiresAt = Date.parse(result?.expiresAt);
      if (!result?.verificationToken || result.email !== email.trim().toLowerCase() || result.phoneNumber !== phoneNumber || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) throw new Error('Phone verification not confirmed.');
      setPhoneProof({ token: result.verificationToken, email: result.email, phoneNumber, expiresAt }); setStep(6);
    } catch (failure) { addToast(failure.response?.data?.message || 'That text code could not be verified. Please try again.', 'error'); }
    finally { phoneLock.current = false; setPhoneBusy(false); }
  }
  async function handleNext() {
    track(
      'registration_step_attempted',
      {
        step,

        step_name:
          getRegistrationStepName(
            step
          ),
      }
    );

  if (step === 1) {
  if (prefillLoading || inviteValidationLock.current) return;
  if (!inviteCode.trim()) {
    addToast(
      'Please enter your invite code.',
      'error'
    );

    setShakeInputs(true);

    setTimeout(() => {
      setShakeInputs(false);
    }, 300);

    return;
  }

  inviteValidationLock.current = true;
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/users/validateInviteCode`,
      {
        inviteCode,
      }
    );

    applyRegistrationPrefill(response.data, inviteCode.trim().toUpperCase());
    setPrefillError('');

    track(
      'registration_step_completed',
      {
        step: 1,
        step_name: 'invite_code',
      }
    );

    setStep(2);
  } catch (err) {
    addToast(
      err.response?.data?.message ||
        'That invite code is not valid.',
      'error'
    );

    setShakeInputs(true);

    setTimeout(() => {
      setShakeInputs(false);
    }, 300);
  }

  inviteValidationLock.current = false;
  return;
}

    if (step === 2) {
  if (sendCodeLockRef.current) {
    return;
  }

  if (
    !firstName.trim() ||
    !lastName.trim() ||
    !email.trim()
  ) {
    addToast(
      'Please enter your name and email.',
      'error'
    );

    setShakeInputs(true);

    setTimeout(() => {
      setShakeInputs(false);
    }, 300);

    return;
  }

  if (applicationEmailUnchanged) {
    track('registration_step_completed', { step: 2, step_name: 'basic_info' });
    setStep(4); // The approved application already verified this email.
    return;
  }

  sendCodeLockRef.current = true;
  setIsSendingCode(true);

  try {
    await axios.post(
      `${process.env.REACT_APP_API_URL}/users/sendCode`,
      {
        firstName,
        lastName,
        email,
        inviteCode,
      }
    );

    addToast(
      `Verification code sent to ${email}`,
      'success'
    );

    track(
      'registration_step_completed',
      {
        step: 2,
        step_name: 'basic_info',
      }
    );

    setStep(3);
  } catch (err) {
    let message;

    if (err.response?.status === 429) {
      message =
        'Too many verification code requests. Please wait before trying again.';
    } else {
      message =
        err.response?.data?.message ||
        'Validation error. Please check your information and invite code.';
    }

    addToast(
      message,
      'error'
    );

    setShakeInputs(true);

    setTimeout(() => {
      setShakeInputs(false);
    }, 300);
  } finally {
    sendCodeLockRef.current = false;
    setIsSendingCode(false);
  }

  return;
}

    if (step === 4) {
      if (
        !password.trim() ||
        !confirmPassword.trim()
      ) {
        addToast(
          'Please enter both password fields',
          'error'
        );

        return;
      }

      if (password.length < 8) {
        addToast(
          'Password must be at least 8 characters long.',
          'error'
        );

        setShakeInputs(true);

        setTimeout(() => {
          setShakeInputs(false);
          setPassword('');
          setConfirmPassword('');

          const passwordField =
            document.getElementById(
              'passwordField'
            );

          passwordField?.focus();
        }, 300);

        return;
      }

      if (
        password !==
        confirmPassword
      ) {
        addToast(
          'Passwords do not match!',
          'error'
        );

        setShakeInputs(true);

        setTimeout(() => {
          setShakeInputs(false);
          setPassword('');
          setConfirmPassword('');

          const passwordField =
            document.getElementById(
              'passwordField'
            );

          passwordField?.focus();
        }, 300);

        return;
      }

      track(
        'registration_step_completed',
        {
          step: 4,
          step_name: 'password',
        }
      );

      setStep(5);
      return;
    }

    if (step === 5.5) { await confirmPhoneVerification(); return; }
    if (step === 5) {
      if (
        phoneNumber.trim().length !==
        10
      ) {
        addToast(
          'Please enter a valid 10-digit phone number.',
          'error'
        );

        return;
      }

      track(
        'registration_step_completed',
        {
          step: 5,
          step_name: 'phone',
        }
      );

      if (phoneIsVerified) setStep(6);
      else await requestPhoneVerification(true);
      return;
    }

    if (step === 7) {
      if (!gender) {
        addToast(
          'Please select your gender',
          'error'
        );

        return;
      }

      if (
        !dateOfBirth ||
        !validateAgeAtLeast18(
          dateOfBirth
        )
      ) {
        addToast(
          'You must be at least 18. Please enter a valid birthday.',
          'error'
        );

        setShakeInputs(true);

        setTimeout(() => {
          setShakeInputs(false);
          setDateOfBirth('');
          setTypedBirthday('');
        }, 300);

        return;
      }

      track(
        'registration_step_completed',
        {
          step: 7,
          step_name: 'gender_dob',
        }
      );

      setStep(8);
      return;
    }

    if (step === 8) {
      if (!profilePicFile && !useApplicationPhoto) {
        addToast(
          'Please upload a valid profile picture (JPG/PNG <= 15MB).',
          'error'
        );

        return;
      }

      track(
        'registration_step_completed',
        {
          step: 8,
          step_name:
            'profile_picture',
        }
      );

      setStep(9);
      return;
    }

    if (step === 9) {
      if (
        interests.length < 3
      ) {
        addToast(
          'Please select at least 3 interests',
          'error'
        );

        return;
      }

      track(
        'registration_step_completed',
        {
          step: 9,
          step_name: 'interests',
        }
      );

      setStep(10);
      return;
    }

    if (step === 10) {
      if (!checkedTerms) {
        addToast(
          'You must agree to T&C first',
          'error'
        );

        return;
      }

      if (
        isSubmitting ||
        submitLockRef.current
      ) {
        return;
      }

      if (!phoneIsVerified) { addToast('Verify your phone number before creating your account.', 'error'); setStep(5); return; }
      submitLockRef.current = true;
      setIsSubmitting(true);

      try {
        const formData =
          new FormData();

        formData.append(
          'firstName',
          firstName
        );

        formData.append(
          'lastName',
          lastName
        );

        formData.append(
          'email',
          email
        );

        formData.append(
          'inviteCode',
          inviteCode
        );

        formData.append(
          'password',
          password
        );

        formData.append(
          'phoneNumber',
          phoneNumber
        );

        formData.append(
          'smsOptIn',
          String(
            smsOptIn === true
          )
        );

        formData.append(
          'gender',
          gender
        );

        formData.append(
          'dateOfBirth',
          birthDateISO(dateOfBirth)
        );

        formData.append(
          'interests',
          JSON.stringify(
            interests
          )
        );

        if (phoneProof?.token) formData.append('phoneVerificationToken', phoneProof.token);
        if (profilePicFile) formData.append('profilePic', profilePicFile);
        if (useApplicationPhoto) formData.append('useApplicationPhoto', 'true');
        if (applicationPrefill) formData.append('instagramHandle', applicationPrefill.instagramHandle);

        const finalizeResp =
          await axios.post(
            `${process.env.REACT_APP_API_URL}/users/finalizeAll`,
            formData,
            {
              headers: {
                'Content-Type':
                  'multipart/form-data',
              },
            }
          );

        const {
          userId,
          sessionId,
          accessToken,
          refreshToken,
        } = finalizeResp.data;

        identifyUser(
          userId,
          {
            email,
            first_name:
              firstName,
          }
        );

        track(
          'registration_completed',
          {
            step: 10,
            step_name: 'terms',
            method: 'email',
            sms_opt_in:
              smsOptIn === true,
          }
        );

        localStorage.setItem(
          'accessToken',
          accessToken
        );

        localStorage.setItem(
          'refreshToken',
          refreshToken
        );

        localStorage.setItem(
          'sessionId',
          sessionId
        );

        localStorage.setItem(
          'userId',
          userId
        );

        handleLoginSuccess(
          userId
        );

        navigate(
          '/events',
          {
            replace: true,
          }
        );
      } catch (err) {
        addToast(
          err.response?.data
            ?.message ||
            err.message,
          'error'
        );

        if (err.response?.data?.reason === 'phone_verification_required') { setPhoneProof(null); setPhoneChallenge(null); setPhoneCode(''); setStep(5); }
        submitLockRef.current = false;
        setIsSubmitting(false);
      }

      return;
    }

    setStep(
      (currentStep) =>
        currentStep + 1
    );
  }


  function handleSmsConsentChoice(
    enabled
  ) {
    setSmsOptIn(
      enabled === true
    );
  }

  function handleSmsConsentContinue() {
    if (
      smsOptIn !== true &&
      smsOptIn !== false
    ) {
      return;
    }

    setShowSmsTermsReminder(
      smsOptIn === false
    );

    track(
      'registration_step_completed',
      {
        step: 6,
        step_name:
          'sms_consent',
        sms_opt_in:
          smsOptIn === true,
      }
    );

    setStep(7);
  }

  function handleBack() {
    if (phoneBusy) return;
    if (step === 5.5 || step === 6) { setStep(5); return; }
    if (step === 4 && applicationEmailUnchanged) { setStep(2); return; }
    if (step > 1) {
      if (step === 6) {
        setSmsOptIn(null);
      }

      setStep(
        (currentStep) =>
          currentStep - 1
      );
    }
  }

  function anyBoxFocused() {
    return (
      segFocus.seg2 ||
      segFocus.seg3 ||
      segFocus.seg4
    );
  }

  function moveFocus(
    currentId,
    direction
  ) {
    const ids = [
      'seg2',
      'seg3',
      'seg4',
    ];

    const currentIndex =
      ids.indexOf(
        currentId
      );

    const nextIndex =
      currentIndex +
      direction;

    if (
      nextIndex >= 0 &&
      nextIndex <
        ids.length
    ) {
      document
        .getElementById(
          ids[nextIndex]
        )
        ?.focus();
    }
  }

  function genderLabel() {
    if (!gender) {
      return '--Select--';
    }

    switch (gender) {
      case 'male':
        return 'Male';

      case 'female':
        return 'Female';

      case 'non-binary':
        return 'Non-binary';

      case 'trans-man':
        return 'Trans-Man';

      case 'trans-woman':
        return 'Trans-Woman';

      default:
        return '--Select--';
    }
  }

  function handleGenderSelect(
    value
  ) {
    setGender(value);
    setShowGenderMenu(false);
  }

  function validateAgeAtLeast18(value) {
    return isAdultBirthDate(value);
  }

  function handleCodeChange(value) {
  setVerificationCode(value);

  if (value.length !== 3) {
    setIsCodeValid(false);
    return;
  }

  axios
    .post(
      `${process.env.REACT_APP_API_URL}/users/verifyCodeInMem`,
      {
        email,
        code: value,
      }
    )
    .then(() => {
      setIsCodeValid(true);

      track(
        'registration_step_completed',
        {
          step: 3,
          step_name: 'verification_code',
        }
      );
    })
    .catch(() => {
      setIsCodeValid(false);
      setShakeInputs(true);

      setTimeout(() => {
        setVerificationCode('');
        setShakeInputs(false);
      }, 300);

      addToast(
        'Invalid code',
        'error'
      );
    });
}
  async function handleSendOrResendCode() {
    if (sendCodeLockRef.current) {
      return;
    }

    sendCodeLockRef.current = true;
    setIsSendingCode(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/users/sendCode`,
        {
          firstName,
          lastName,
          email,
          inviteCode,
        }
      );

      addToast(
        `Verification code re-sent to ${email}!`,
        'success'
      );

      setIsCodeValid(false);
      setVerificationCode('');
    } catch (err) {
      const message =
        err.response?.data
          ?.message ||
        'Too many requests. Please wait before trying again.';

      addToast(
        message,
        'error'
      );
    } finally {
      sendCodeLockRef.current = false;
      setIsSendingCode(false);
    }
  }

  function handleBirthdayInput(
    raw
  ) {
    let digits = raw.replace(
      /\D/g,
      ''
    );

    if (
      digits.length > 8
    ) {
      digits =
        digits.slice(0, 8);
    }

    if (
      digits.length > 2
    ) {
      digits =
        `${digits.slice(
          0,
          2
        )}/${digits.slice(2)}`;
    }

    if (
      digits.length > 5
    ) {
      digits =
        `${digits.slice(
          0,
          5
        )}/${digits.slice(5)}`;
    }

    setTypedBirthday(
      digits
    );

    setDateOfBirth(
      digits
    );
  }

  function handleProfilePicChange(
    event
  ) {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    if (
      file.size >
      MAX_SIZE_BYTES
    ) {
      addToast(
        'File too large. Max size is 15 MB.',
        'error'
      );

      event.target.value =
        null;

      return;
    }

    if (
      !ALLOWED_TYPES.includes(
        file.type
      )
    ) {
      addToast(
        'Unsupported format. Only JPEG or PNG allowed.',
        'error'
      );

      event.target.value =
        null;

      return;
    }

    setProfilePicFile(
      file
    );

    const reader =
      new FileReader();

    reader.onloadend = () => {
      setProfilePicPreview(
        reader.result
      );
    };

    reader.readAsDataURL(
      file
    );
  }

  function triggerFilePicker() {
    fileInputRef.current?.click();
  }

  function renderProfileActionSheet() {
    if (
      !showProfileActionSheet
    ) {
      return null;
    }

    return (
      <>
        <div
          style={
            actionSheetStyles.backdrop
          }
          onClick={() => {
            setShowProfileActionSheet(
              false
            );
          }}
        >
          <div
            style={
              actionSheetStyles.sheetContainer
            }
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div
              style={
                actionSheetStyles.optionContainer
              }
            >
              <div
                style={{
                  ...actionSheetStyles.optionRow,
                  ...actionSheetStyles.optionRowTop,
                }}
                onClick={() => {
                  setShowProfileActionSheet(
                    false
                  );

                  triggerFilePicker();
                }}
              >
                <span>
                  Photo Library
                </span>

                <FaRegImage
                  style={
                    actionSheetStyles.iconRight
                  }
                />
              </div>

              <div
                style={{
                  ...actionSheetStyles.optionRow,
                  ...actionSheetStyles.optionRowBottom,
                }}
                onClick={() => {
                  setShowProfileActionSheet(
                    false
                  );

                  triggerFilePicker();
                }}
              >
                <span>
                  Choose from Files
                </span>

                <FaFolderOpen
                  style={
                    actionSheetStyles.iconRight
                  }
                />
              </div>
            </div>

            <div
              style={
                actionSheetStyles.cancelRow
              }
              onClick={() => {
                setShowProfileActionSheet(
                  false
                );
              }}
            >
              Cancel
            </div>
          </div>
        </div>

        <style>
          {`
            @keyframes slideUp {
              from {
                transform: translateY(100%);
                opacity: 0;
              }

              to {
                transform: translateY(0);
                opacity: 1;
              }
            }
          `}
        </style>
      </>
    );
  }

  return (
    <div
      style={{
        ...pageStyle,

        display: 'flex',
        flexDirection:
          'column',

        minHeight:
          '100.25vh',
      }}
    >
      {step === 1 && (
        <>
          <div
            style={{
              ...topBarStyleStep1,

              marginBottom:
                '1rem',

              zIndex: 4,
            }}
          >
            <button
              type="button"
              style={
                blackBackButtonStyle
              }
              onClick={() => {
                navigate(
                  '/events'
                );
              }}
            >
              <IoArrowBackSharp />
            </button>

            <div
              style={
                toggleContainerStyle
              }
            >
              <div
                style={
                  toggleItemStyle(
                    false
                  )
                }
                onClick={() => {
                  navigate(
                    '/login'
                  );
                }}
              >
                Login
              </div>

              <div
                style={
                  toggleItemStyle(
                    true
                  )
                }
              >
                Sign Up
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'flex',

              justifyContent:
                'center',

              marginTop:
                '1.5rem',
            }}
          >
            <TribeLogo
              style={{
                width: 100,
                height: 'auto',
                cursor: 'pointer',
              }}
              onClick={() => {
                navigate(
                  '/events'
                );
              }}
            />
          </div>
        </>
      )}

      {step > 1 &&
        step <= 10 && (
          <header
            style={
              headerContainerStyle
            }
          >
            <div
              style={
                headerLogoRowStyle
              }
            >
              <TribeLogo
                style={
                  logoSvgStyle
                }
              />
            </div>

            <div
              style={
                headerNavRowStyle
              }
            >
              <button
                type="button"
                onClick={() => {
                  if (
                    isSubmitting
                  ) {
                    return;
                  }

                  setShowUnsavedSheet(
                    true
                  );
                }}
                disabled={
                  isSubmitting
                }
                style={{
                  ...previousBackButtonStyle,

                  opacity:
                    isSubmitting
                      ? 0.6
                      : 1,

                  cursor:
                    isSubmitting
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                <IoArrowBackSharp />
              </button>

              <div
                style={
                  pageTitlePlainStyle
                }
              >
                Register
              </div>

              <div
                style={
                  progressContainerStyle
                }
              >
                <ProgressPill
                  step={registrationSteps.indexOf(step) + 1}
                  total={registrationSteps.length}
                />
              </div>
            </div>
          </header>
        )}

      <div
        ref={registrationContentRef}
        style={{
          ...containerStyle,
          flex: '1',
        }}
      >
        {prefillLoading ? <p role="status" style={{ textAlign: 'center', padding: '40px 20px' }}>Loading your application details…</p> : <>
        {prefillError ? <p role="alert" style={{ maxWidth: 420, margin: '0 auto 20px', padding: 12, color: '#9b392a' }}>{prefillError}</p> : null}
        {renderStepContent({
          step,

          firstName,
          setFirstName,

          lastName,
          setLastName,

          email,
          setEmail: changeRegistrationEmail,
          useApplicationPhoto,

          inviteCode,
          setInviteCode: changeInviteCode,

          verificationCode,
          setVerificationCode,

          isCodeValid,
          setIsCodeValid,

          shakeInputs,
          setShakeInputs,

          shakeCode,
          setShakeCode,

          password,
          setPassword,

          confirmPassword,
          setConfirmPassword,

          phoneNumber,
          setPhoneNumber,
          phoneCode, setPhoneCode, phoneBusy, phoneWaitSeconds, phoneChallenge,
          requestPhoneVerification,

          smsOptIn,
          setSmsOptIn,
          showSmsTermsReminder,
          setShowSmsTermsReminder,
          handleSmsConsentChoice,
          handleSmsConsentContinue,

          segFocus,
          setSegFocus,

          gender,
          setGender,

          showGenderMenu,
          setShowGenderMenu,

          genderLabel,
          handleGenderSelect,

          dateOfBirth,
          setDateOfBirth,

          typedBirthday,
          setTypedBirthday,

          handleBirthdayInput,

          fileInputRef,

          profilePicFile,
          setProfilePicFile,

          profilePicPreview,
          setProfilePicPreview,

          showProfileActionSheet,
          setShowProfileActionSheet,

          renderProfileActionSheet,
          handleProfilePicChange,
          triggerFilePicker,

          MAX_SIZE_BYTES,
          ALLOWED_TYPES,

          interests,
          setInterests,

          customInterest,
          setCustomInterest,

          checkedTerms,
          setCheckedTerms,

          handleNext,
          handleBack,

          handleSendOrResendCode,
          handleCodeChange,

          navigate,
          addToast,

          handleLoginSuccess,

          anyBoxFocused,
          moveFocus,

          validateAgeAtLeast18,
          isSubmitting,
          isSendingCode,
        })}</>}
      </div>

      {step === 1 && (
        <MinimalFooter />
      )}

      {showUnsavedSheet && (
        <UnsavedChangesBottomSheet
          onCancel={() => {
            setShowUnsavedSheet(
              false
            );
          }}
          onDiscard={() => {
            setShowUnsavedSheet(
              false
            );

            setStep(1);
            setFirstName('');
            setLastName('');
            setEmail('');
            setInviteCode('');
            setApplicationPrefill(null); setPrefillError('');
            setVerificationCode('');
            setPassword('');
            setConfirmPassword('');
            setPhoneNumber('');
            setSmsOptIn(null);
            setShowSmsTermsReminder(false);
            setGender('');
            setDateOfBirth('');
            setTypedBirthday('');
            setInterests([]);
            setCheckedTerms(false);
            setProfilePicFile(null);
            setProfilePicPreview(null);

            navigate(
              '/register'
            );
          }}
        />
      )}
    </div>
  );
}